import { useCallback, useState } from 'react';

import { InboxEntry } from '@this+that/models';
import log from 'loglevel';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useMoveInboxEntryToTaskGroupService } from '../../services/inboxEntries.service';
import { useInboxEntriesStore, useUnreadStore } from '../../store';

interface MoveOptions {
  keepModalEntry?: boolean;
}

const syncActiveTaskGroupSlice = (
  updatedEntry: InboxEntry,
  activeTaskGroupId: string | null,
  targetTaskGroupId: string | null,
  updateInboxEntry: (entry: InboxEntry) => void,
  removeInboxEntry: (id: string) => void,
  removeInboxEntryFromActiveList: (entry: InboxEntry) => void,
  options: MoveOptions,
) => {
  if (!activeTaskGroupId) {
    return;
  }

  if (activeTaskGroupId === targetTaskGroupId) {
    updateInboxEntry(updatedEntry);
  } else if (options.keepModalEntry) {
    removeInboxEntryFromActiveList(updatedEntry);
  } else {
    removeInboxEntry(updatedEntry.id);
  }
};

export const useMoveInboxEntryToTaskGroup = () => {
  const { moveInboxEntryToTaskGroup: moveService } =
    useMoveInboxEntryToTaskGroupService();
  const { removeInboxEntry, updateInboxEntry, removeInboxEntryFromActiveList } =
    useActiveInboxEntriesSlice();
  const activeTaskGroupId = useInboxEntriesStore(
    (state) => state.taskManager.activeTaskGroup.taskGroupId,
  );
  const { markAsRead } = useUnreadStore();
  const [isMovingToTaskGroup, setIsMovingToTaskGroup] = useState(false);

  const moveToTaskGroup = useCallback(
    async (
      inboxEntryId: string,
      taskGroupId: string | null,
      options: MoveOptions = {},
    ): Promise<boolean> => {
      setIsMovingToTaskGroup(true);
      try {
        if (!taskGroupId) {
          log.error('taskGroupId invalid or undefined');
          return false;
        }

        const updatedEntry = await moveService(inboxEntryId, taskGroupId);

        if (!updatedEntry) {
          return false;
        }

        syncActiveTaskGroupSlice(
          updatedEntry,
          activeTaskGroupId,
          taskGroupId,
          updateInboxEntry,
          removeInboxEntry,
          removeInboxEntryFromActiveList,
          options,
        );

        if (activeTaskGroupId) {
          markAsRead(inboxEntryId, activeTaskGroupId);
        }

        return true;
      } catch (error) {
        log.error('Error moving inbox entry to task group:', error);
        return false;
      } finally {
        setIsMovingToTaskGroup(false);
      }
    },
    [
      moveService,
      updateInboxEntry,
      removeInboxEntry,
      removeInboxEntryFromActiveList,
      activeTaskGroupId,
      markAsRead,
    ],
  );

  return { moveToTaskGroup, isMovingToTaskGroup };
};
